import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { searchApi, type SearchResultItem } from '../api/search'
import { Icon } from './ui'

/**
 * TASK-0105 (REQ-039-001 "always-available global search bar"): rendered once from `Topbar` in
 * `App.tsx`, which itself is mounted outside `<Routes>` — so this widget is genuinely persistent
 * across every authenticated page, not re-mounted per route.
 *
 * Mirrors `SearchController.cs`'s own 2-character minimum (see `MinQueryLength` there) so the
 * frontend never fires a request the backend would answer with an empty result set anyway — see
 * the length check at the top of the debounce effect below, which runs synchronously on every
 * keystroke and returns before any timer is ever scheduled.
 */
const MIN_QUERY_LENGTH = 2

/**
 * 300ms: comfortably inside the commonly-cited 200-400ms "feels instant, not laggy" range for
 * search-as-you-type widgets, and long enough to collapse a fast typist's burst of keystrokes
 * (each keystroke resets the timer, see the effect below) into a single request. The backend's
 * per-request cost here is three unindexed `ILIKE`-equivalent scans (see `SearchController.cs`'s
 * own remarks on why `REQ-244`'s dedicated index is explicitly out of scope for this endpoint), so
 * erring slightly longer than the 250ms floor the task card suggests trades a small amount of
 * perceived latency for meaningfully fewer redundant scans without feeling sluggish.
 */
const DEBOUNCE_MS = 300

/**
 * TASK-0147: `SearchResultItem['type']` (in `api/search.ts`) did not yet carry the 4th,
 * backend-added value — that file was outside TASK-0147's allowed_paths, so rather than widen it
 * there this component locally widens the type it actually works with to include `"supplypoint"`
 * (see `SearchController.cs`'s `SearchSupplyPointsAsync`). `SearchResultItem['type']` is a strict
 * subset of `ResultType`, so every existing `SearchResultItem` value (and the API response, whose
 * real runtime `type` field is trusted the same way it already was before this task) is still
 * assignable wherever `ResultType` is expected below.
 *
 * TASK-0168: `api/search.ts` IS inside this task's allowed_paths, so the 5th value (`"document"`)
 * is added to the shared interface there and flows into `ResultType` through
 * `SearchResultItem['type']` — no second local widening is needed for it, and listing it again here
 * would just be a duplicate union member. The `"supplypoint"` widening stays exactly as TASK-0147
 * left it.
 */
// F-076: 'contact' (crm.contacts, i clienti veri) allargato qui come 'supplypoint', senza toccare api/search.ts.
type ResultType = SearchResultItem['type'] | 'supplypoint' | 'contact'

/**
 * TASK-0147: for a `"supplypoint"` result, `item.id` is deliberately the owning Customer's id, not
 * the SupplyPoint's own id — `SupplyPoint` has no standalone detail route in this frontend (it only
 * ever renders nested inside `CustomerDetailPage.tsx`), so the backend already substitutes
 * `CustomerId` into the result's `id` for exactly this reason (see `SearchSupplyPointsAsync`'s XML
 * doc in `SearchController.cs`). Navigating to the customer page is therefore correct here, not a
 * shortcut.
 *
 * TASK-0168: a `"document"` result follows the same "borrowed target" idea one step further. There
 * is no `/documents/:id` route either — `DocumentsPage.tsx` is a Drive browser filtered by the
 * `?entityType=…&entityId=…` query pair (verified by reading that page: it initialises its filter
 * from `searchParams.get('entityType')`/`searchParams.get('entityId')`) — and unlike a supply
 * point, whose owner is always a Customer, a document's owner may be a Contract, Lead, Customer or
 * Company. So the backend sends BOTH halves: `id` is `Document.EntityId` and `documentEntityType`
 * is `Document.EntityType`. When the type is somehow missing, the param is omitted rather than sent
 * empty, letting the page fall back to its own default exactly as a bare `/documents` visit does.
 *
 * REQ-243-001: `"company"` has a real detail route (`/companies/:id`, same shape as
 * customer/lead/contract) so it needs no borrowed target at all. `"invoice"` and `"batch"` do NOT —
 * verified directly against `ReportsPage.tsx`/`BatchesPage.tsx`: both are plain list pages with no
 * `useSearchParams`/id-based filter or highlight of any kind, unlike `DocumentsPage.tsx`. So unlike
 * every other type above, there is no per-row target to borrow OR land on — the best this component
 * can do today is send the caller to the list page itself (`/reports`, `/batches`) and let them find
 * the matched row by the code/number shown in the result's own label. Extending those two pages to
 * accept a highlight/filter id is a separate, frontend-only follow-up.
 *
 * REQ-243-001 review-fix: `"movement"` (a `CommissionMovement` ledger row) is the same shape as
 * `"invoice"`/`"batch"` — `CommissionsPage.tsx` (verified directly) is likewise a plain list with no
 * id-based filter, so this falls back to `/commissions` rather than a per-row target.
 */
function detailPath(item: { type: ResultType; id: number; documentEntityType?: string | null }): string {
  switch (item.type) {
    case 'customer': return `/customers/${item.id}`
    case 'contact': return `/contacts/${item.id}`
    case 'lead': return `/leads/${item.id}`
    case 'contract': return `/contracts/${item.id}`
    case 'supplypoint': return `/customers/${item.id}`
    case 'document': {
      const params = new URLSearchParams()
      if (item.documentEntityType) params.set('entityType', item.documentEntityType)
      params.set('entityId', String(item.id))
      return `/documents?${params.toString()}`
    }
    case 'company': return `/companies/${item.id}`
    case 'invoice': return '/reports'
    case 'batch': return '/batches'
    case 'movement': return '/commissions'
  }
}

/** Fixed display order + Italian headers, independent of whatever order the backend happens to
 * return results in (today it's
 * customer/lead/contract/supplypoint/document/company/invoice/batch/movement, but grouping
 * explicitly here doesn't depend on that staying true). */
const GROUPS: Array<{ type: ResultType; heading: string }> = [
  { type: 'customer', heading: 'Clienti' },
  { type: 'contact', heading: 'Contatti cliente' },
  { type: 'lead', heading: 'Lead' },
  { type: 'contract', heading: 'Contratti' },
  { type: 'supplypoint', heading: 'Punti di Fornitura' },
  { type: 'document', heading: 'Documenti' },
  { type: 'company', heading: 'Aziende' },
  { type: 'invoice', heading: 'Fatture' },
  { type: 'batch', heading: 'Lotti' },
  { type: 'movement', heading: 'Movimenti' },
]

export function GlobalSearch() {
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Bumped only at the moment a request actually fires (inside the debounce callback below), so a
  // slower in-flight request that resolves after a newer one can recognize itself as stale and
  // avoid clobbering fresher results — a plain "cancel the previous timer" isn't enough once two
  // fetches are simultaneously in flight.
  const requestSeq = useRef(0)

  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResultItem[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
      debounceTimer.current = null
    }

    const trimmed = query.trim()
    if (trimmed.length < MIN_QUERY_LENGTH) {
      // Same "200 OK, empty results" principle the backend applies below its own threshold: no
      // request, no error — just nothing to show yet. Closing here (rather than only clearing
      // results) covers the "query drops below minimum length" close condition from the task card.
      setResults([])
      setLoading(false)
      setOpen(false)
      return
    }

    setOpen(true)
    setLoading(true)

    debounceTimer.current = setTimeout(() => {
      const seq = ++requestSeq.current
      searchApi.search(trimmed)
        .then(res => {
          if (seq !== requestSeq.current) return // superseded by a newer keystroke's request
          setResults(res.results)
        })
        .catch(err => {
          if (seq !== requestSeq.current) return
          // A failed request is not "no results" (which is a normal, non-error outcome) — but per
          // the task card the dropdown must never look like an error state either. Logging is the
          // residual signal for a developer; the UI just falls back to the same empty-results
          // rendering a genuine zero-match search would show.
          console.error('GlobalSearch: search request failed', err)
          setResults([])
        })
        .finally(() => {
          if (seq === requestSeq.current) setLoading(false)
        })
    }, DEBOUNCE_MS)

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [query])

  // Click outside closes the dropdown.
  useEffect(() => {
    if (!open) return
    function handlePointerDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  const grouped = useMemo(() => {
    return GROUPS
      .map(g => ({ ...g, items: results.filter(r => r.type === g.type) }))
      .filter(g => g.items.length > 0)
  }, [results])

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  function handleSelect(item: SearchResultItem) {
    setOpen(false)
    setQuery('')
    setResults([])
    navigate(detailPath(item))
  }

  const trimmedLength = query.trim().length
  const showDropdown = open && trimmedLength >= MIN_QUERY_LENGTH
  // Stale results from a previous (shorter) query are deliberately left on screen while a new
  // request is in flight — "in flight" already looks like "in flight" via the spinner next to the
  // input, so there's no need to blank the list and re-flash it on every keystroke. The dedicated
  // loading message below only covers the one case where there is nothing else to show yet: the
  // very first request after crossing the minimum length, before any results have ever arrived.
  const showLoadingEmpty = showDropdown && loading && results.length === 0
  const showNoResults = showDropdown && !loading && results.length === 0

  return (
    <div className="md-global-search" ref={containerRef} role="search">
      <Icon name="search" size={24} className="md-global-search-icon" />
      <input
        type="text"
        className="md-global-search-input"
        placeholder="Cerca clienti, lead, contratti…"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => { if (trimmedLength >= MIN_QUERY_LENGTH) setOpen(true) }}
        onKeyDown={handleKeyDown}
        aria-label="Ricerca globale"
        aria-expanded={showDropdown}
        role="combobox"
        aria-controls="md-global-search-listbox"
        aria-autocomplete="list"
      />
      {loading && <span className="md-global-search-spinner" aria-hidden="true" />}

      {showDropdown && (
        <div className="md-global-search-dropdown" id="md-global-search-listbox" role="listbox">
          {showLoadingEmpty && (
            <div className="md-global-search-empty">Ricerca in corso...</div>
          )}
          {showNoResults && (
            <div className="md-global-search-empty">Nessun risultato per "{query.trim()}"</div>
          )}
          {!showLoadingEmpty && !showNoResults && grouped.map(group => (
            <div key={group.type} className="md-global-search-group">
              <div className="md-global-search-group-heading">{group.heading}</div>
              {group.items.map((item, index) => (
                <button
                  // TASK-0168: the index is part of the key because `type`+`id` is no longer unique
                  // per result. A `"document"` result's `id` is its OWNING entity's id (see
                  // `detailPath`), so two documents attached to the same customer that both match
                  // the query produce two legitimately distinct results sharing one `type`+`id`
                  // pair — duplicate React keys, and the second row silently mis-reconciled. The
                  // list is fully re-derived from `results` on every query (no per-row state, no
                  // reordering in place), so an index-bearing key is safe here.
                  key={`${item.type}-${item.id}-${index}`}
                  type="button"
                  role="option"
                  aria-selected="false"
                  className="md-global-search-result"
                  onClick={() => handleSelect(item)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
